import React, { useEffect, useState } from 'react';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Typography,
} from 'antd';
import { vendorApi, VendorUpdateError } from '@/utils/vendorApi';
import { canAssignVendorTag, canUpdateVendor, canUpdateVendorRouting } from '@/store/vendorAccess';
import { getSourceRoutes, hasStoredSourceRoutes } from '@/utils/sourceRoutes';
import SourceRoutesEditor, {
  blankSourceRoute,
  buildSourceEndpointOptions,
  normalizeSourceRoutes,
  sourceRoutesUnchanged,
} from '@/components/vendor/SourceRoutesEditor';
import {
  Authorization,
  SmbEndpointOption,
  SourceRoute,
  VendorItem,
  VendorUpdateRequest,
  VendorUpdateResponse,
} from '@/types';

const { Title } = Typography;

const EMAIL_RULE = { type: 'email' as const, message: 'Enter a valid email address' };

interface VendorEditValues {
  vendor_email: string;
  success_email: string;
  failure_email: string;
  source_endpoint_id?: string;
  source_routes?: SourceRoute[];
  tag?: string;
}

interface VendorEditFormProps {
  vendor: VendorItem;
  authorization: Authorization | null;
  onUpdated: (vendor: VendorItem) => void;
}

function editorRoutes(vendor: VendorItem): SourceRoute[] {
  const routes = getSourceRoutes(vendor);
  return routes.length > 0 ? routes : [blankSourceRoute()];
}

/**
 * Only the PATCH allowlist. An unchanged source_endpoint_id is omitted so a
 * resend does not re-check the endpoint tag. source_routes is sent when the
 * editor differs from getSourceRoutes. Flat folder and pattern are never sent.
 *
 * A vendor with no stored list derives its outbound route from the flat
 * source_endpoint_id. Changing that inbound id would move pickup, so the
 * current editor rows are sent too and pin the old route. A stored list is
 * already independent. The blank placeholder is not a route to pin.
 */
export function buildVendorUpdateRequest(
  values: VendorEditValues,
  vendor: VendorItem,
  access: { contact: boolean; routing: boolean; assignTag?: boolean },
): VendorUpdateRequest {
  const body: VendorUpdateRequest = {};
  if (access.assignTag) {
    const next = (values.tag ?? '').trim();
    const current = (vendor.tag ?? '').trim();
    if (next !== current) {
      body.tag = next;
    }
  }
  if (access.contact) {
    body.vendor_email = values.vendor_email.trim();
    body.success_email = values.success_email.trim();
    body.failure_email = values.failure_email.trim();
  }
  if (!access.routing) {
    return body;
  }
  const sourceEndpointId = values.source_endpoint_id?.trim();
  const storedEndpointId = (vendor.source_endpoint_id ?? '').trim();
  const inboundChanged = Boolean(sourceEndpointId && sourceEndpointId !== storedEndpointId);
  if (inboundChanged && sourceEndpointId) {
    body.source_endpoint_id = sourceEndpointId;
  }
  const baseline = getSourceRoutes(vendor);
  const routesChanged = !sourceRoutesUnchanged(values.source_routes, baseline);
  const placeholder = sourceRoutesUnchanged(values.source_routes, []);
  const pinLegacyRoute = inboundChanged && !hasStoredSourceRoutes(vendor) && !placeholder;
  if (routesChanged || pinLegacyRoute) {
    body.source_routes = normalizeSourceRoutes(values.source_routes);
  }
  return body;
}

function fieldsFromVendor(vendor: VendorItem): VendorEditValues {
  return {
    vendor_email: vendor.vendorEmail ?? '',
    success_email: vendor.successEmail ?? '',
    failure_email: vendor.failureEmail ?? '',
    source_endpoint_id: vendor.source_endpoint_id ?? undefined,
    source_routes: editorRoutes(vendor),
    tag: vendor.tag ?? '',
  };
}

/** Overlay the minimal PATCH body onto the vendor already on the page. */
export function applyVendorUpdate(vendor: VendorItem, updated: VendorUpdateResponse): VendorItem {
  return {
    ...vendor,
    vendorId: updated.vendorId || vendor.vendorId,
    hostname: updated.hostname || vendor.hostname,
    vendorEmail: updated.vendor_email || vendor.vendorEmail,
    successEmail: updated.success_email || vendor.successEmail,
    failureEmail: updated.failure_email || vendor.failureEmail,
    source_endpoint_id: updated.source_endpoint_id || vendor.source_endpoint_id || null,
    source_folder: updated.source_folder || vendor.source_folder || null,
    file_pattern: updated.file_pattern || vendor.file_pattern || null,
    updatedAt: updated.updatedAt || vendor.updatedAt,
    ...(Array.isArray(updated.source_routes) ? { source_routes: updated.source_routes } : {}),
    ...(typeof updated.active === 'boolean' ? { active: updated.active } : {}),
    ...(updated.tag !== undefined ? { tag: updated.tag } : {}),
  };
}

const VendorEditForm: React.FC<VendorEditFormProps> = ({ vendor, authorization, onUpdated }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<VendorEditValues>();
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [endpoints, setEndpoints] = useState<SmbEndpointOption[]>([]);
  const [endpointsLoading, setEndpointsLoading] = useState(false);
  const [endpointsUnavailable, setEndpointsUnavailable] = useState(false);

  const canUpdateContact = canUpdateVendor(authorization, vendor.bu);
  const canUpdateRouting = canUpdateVendorRouting(authorization, vendor.bu);
  const canAssignTag = canAssignVendorTag(authorization, vendor.bu);
  const canEdit = canUpdateContact || canUpdateRouting || canAssignTag;
  const [tagChoices, setTagChoices] = useState<string[]>([]);

  useEffect(() => {
    form.setFieldsValue(fieldsFromVendor(vendor));
  }, [form, vendor]);

  useEffect(() => {
    if (!canUpdateRouting) {
      return;
    }
    let cancelled = false;
    setEndpointsLoading(true);
    vendorApi.listEndpoints()
      .then((rows) => {
        if (!cancelled) {
          setEndpoints(rows);
          setEndpointsUnavailable(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEndpointsUnavailable(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setEndpointsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [canUpdateRouting]);

  useEffect(() => {
    if (!canAssignTag || !vendor.bu) {
      setTagChoices([]);
      return;
    }
    let cancelled = false;
    vendorApi.listTags(vendor.bu)
      .then((rows) => {
        if (!cancelled) {
          setTagChoices(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTagChoices([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [canAssignTag, vendor.bu]);

  if (!canEdit) {
    return null;
  }

  const startEditing = () => {
    form.setFieldsValue(fieldsFromVendor(vendor));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    form.setFieldsValue(fieldsFromVendor(vendor));
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <Button style={{ marginTop: 24 }} onClick={startEditing}>
        Edit Vendor
      </Button>
    );
  }

  const endpointOptions = buildSourceEndpointOptions(endpoints, [
    vendor.source_endpoint_id,
    ...getSourceRoutes(vendor).map((route) => route.endpoint_id),
  ]);

  const onFinish = async (values: VendorEditValues) => {
    const body = buildVendorUpdateRequest(values, vendor, {
      contact: canUpdateContact,
      routing: canUpdateRouting,
      assignTag: canAssignTag,
    });
    setSubmitting(true);
    try {
      const updated = await vendorApi.patchVendor(
        vendor.vendorId,
        vendor.hostname || 'default',
        body,
      );
      onUpdated(applyVendorUpdate(vendor, updated));
      message.success('Vendor updated');
      setIsEditing(false);
    } catch (error: unknown) {
      if (error instanceof VendorUpdateError) {
        const text = error.message || 'Update failed';
        message.error(error.errorCode ? `${error.errorCode}: ${text}` : text);
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'Failed to update vendor';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Title level={4} style={{ marginTop: 24 }}>Edit vendor</Title>
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          initialValues={fieldsFromVendor(vendor)}
        >
          <Form.Item
            name="vendor_email"
            label="Vendor Email"
            rules={canUpdateContact ? [{ required: true, message: 'Vendor email is required' }, EMAIL_RULE] : []}
          >
            <Input placeholder="onboarding@vendor.com" disabled={!canUpdateContact || submitting} />
          </Form.Item>
          <Form.Item
            name="success_email"
            label="Default Success Email"
            extra="Used for any route that doesn't have its own list. A route with its own list sends only to that list."
            rules={canUpdateContact ? [{ required: true, message: 'Success email is required' }, EMAIL_RULE] : []}
          >
            <Input placeholder="success@vendor.com" disabled={!canUpdateContact || submitting} />
          </Form.Item>
          <Form.Item
            name="failure_email"
            label="Default Failure Email"
            extra="Used for any route that doesn't have its own list. A route with its own list sends only to that list."
            rules={canUpdateContact ? [{ required: true, message: 'Failure email is required' }, EMAIL_RULE] : []}
          >
            <Input placeholder="ops@vendor.com" disabled={!canUpdateContact || submitting} />
          </Form.Item>
          {canAssignTag && (
            <Form.Item
              name="tag"
              label="Tag"
              extra="Optional. Organizational only. It does not change who can access this vendor."
            >
              <Select
                allowClear
                placeholder="No tag"
                disabled={submitting}
                options={tagChoices.map((tag) => ({ value: tag, label: tag }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="source_endpoint_id"
            label="Inbound delivery endpoint"
            extra={
              endpointsUnavailable
                ? 'Inbound files are delivered to this endpoint. Source shares could not be loaded.'
                : 'Inbound files are delivered to this endpoint.'
            }
          >
            <Select
              allowClear
              placeholder="Select an endpoint"
              loading={endpointsLoading}
              disabled={!canUpdateRouting || endpointsUnavailable || submitting}
              options={endpointOptions}
            />
          </Form.Item>
          <SourceRoutesEditor
            options={endpointOptions}
            loading={endpointsLoading}
            unavailable={endpointsUnavailable}
            disabled={!canUpdateRouting || submitting}
            baseline={getSourceRoutes(vendor)}
          />
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Save changes
              </Button>
              <Button onClick={cancelEditing} disabled={submitting}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </>
  );
};

export default VendorEditForm;
